/**
 * Shipment Map Service
 * 
 * Manages Mapbox GL map instance for shipment tracking visualization.
 * Handles markers, route rendering, and map controls.
 */

import mapboxgl from 'mapbox-gl';
import type { Feature, LineString } from 'geojson';
import { calculateStraightDistance, type Coordinate } from '../utils/map-geometry';

/**
 * Map configuration options
 */
export interface MapConfig {
  accessToken: string;
  container: string;
  style?: string;
  lightPreset?: 'day' | 'night';
  projection?: 'globe' | 'mercator';
  zoom?: number;
  attributionControl?: boolean;
}

/**
 * Marker types for shipment tracking
 */
export enum MarkerType {
  ORIGIN = 'origin',
  CURRENT_LOCATION = 'current',
  DESTINATION = 'destination',
  WAYPOINT = 'waypoint'
}

/**
 * Layer configuration
 */
interface LayerConfig {
  id: string;
  color: {
    light: string;
    dark: string;
  };
  width: number;
  dashArray?: number[];
  emissiveStrength?: {
    light: number;
    dark: number;
  };
}

/**
 * ShipmentMap - Manages Mapbox GL instance for shipment visualization
 * 
 * @example
 * ```typescript
 * const map = new ShipmentMap({
 *   accessToken: 'pk.xxx',
 *   container: 'map'
 * });
 * 
 * map.init();
 * map.addMarker(MarkerType.ORIGIN, [-80.495565, 43.455680]);
 * map.addRoute(pathFeature);
 * ```
 */
export class ShipmentMap {
  private map: mapboxgl.Map | null = null;
  private markers: Map<MarkerType, mapboxgl.Marker> = new Map();
  private config: MapConfig;
  private isLoaded: boolean = false;
  private theme: 'light' | 'dark' = 'light';
  private pendingCenter: { current: Coordinate; destination: Coordinate } | null = null;
  private lastCenterCoords: { current: Coordinate; destination: Coordinate } | null = null;
  
  // Default configuration
  private static readonly DEFAULT_CONFIG: Partial<MapConfig> = {
    style: 'mapbox://styles/mapbox/standard',
    lightPreset: 'day',
    projection: 'mercator',
    zoom: 1,
    attributionControl: false
  };

  // Default origin (Voltera office location)
  private static readonly DEFAULT_ORIGIN: Coordinate = [-80.495565, 43.455680];

  // Layer configurations
  private static readonly LAYERS: Record<string, LayerConfig> = {
    route: {
      id: 'route',
      color: {
        light: '#0b72b9',
        dark: '#38bdf8'
      },
      width: 2.5,
      emissiveStrength: {
        light: 0.35,
        dark: 0.95
      }
    },
    'route-final': {
      id: 'route-final',
      color: {
        light: '#0b72b9',
        dark: '#7dd3fc'
      },
      width: 2.5,
      emissiveStrength: {
        light: 0.35,
        dark: 0.95
      }
    },
    planned: {
      id: 'planned',
      color: {
        light: '#64748b',
        dark: '#cbd5e1'
      },
      width: 1.5,
      dashArray: [4, 2],
      emissiveStrength: {
        light: 0.15,
        dark: 0.75
      }
    }
  };

  constructor(config: MapConfig) {
    this.config = { ...ShipmentMap.DEFAULT_CONFIG, ...config };
    mapboxgl.accessToken = this.config.accessToken;
    this.theme = this.config.lightPreset === 'night' ? 'dark' : 'light';
  }

  /**
   * Initialize the map instance
   */
  init(): void {
    if (this.map) {
      console.warn('[ShipmentMap] Map already initialized');
      return;
    }

    const mapOptions: mapboxgl.MapOptions & {
      config?: {
        basemap?: {
          lightPreset?: 'day' | 'night';
          theme?: 'default';
          show3dObjects?: boolean;
        };
      };
    } = {
      container: this.config.container,
      style: this.config.style!,
      config: {
        basemap: {
          theme: 'default',
          lightPreset: this.config.lightPreset || 'day',
          show3dObjects: false
        }
      },
      projection: this.config.projection!,
      zoom: this.config.zoom!,
      attributionControl: this.config.attributionControl!
    };

    this.map = new mapboxgl.Map(mapOptions);

    const configureMapStyle = (): void => {
      this.disableMapFog();
      this.applyBasemapTheme();
      this.updateRouteLayerPaint();
    };

    // Track when map is fully loaded (tiles, style, etc.)
    this.map.on('load', () => {
      this.isLoaded = true;
      configureMapStyle();
      
      // If there's a pending center operation, execute it now
      if (this.pendingCenter) {
        this.centerOnCurrentLocation(
          this.pendingCenter.current, 
          this.pendingCenter.destination
        );
        this.pendingCenter = null;
      }
    });

    this.map.on('style.load', configureMapStyle);

    if (this.map.isStyleLoaded()) {
      configureMapStyle();
    }
  }

  /**
   * Get the underlying Mapbox GL map instance
   */
  getMap(): mapboxgl.Map | null {
    return this.map;
  }

  /**
   * Apply a day/night basemap preset and refresh route contrast.
   */
  setTheme(theme: 'light' | 'dark'): void {
    this.theme = theme;
    this.config.lightPreset = theme === 'dark' ? 'night' : 'day';
    // Theme may be selected before Mapbox has finished loading its style.
    // style.load will apply the latest stored theme once it is safe to mutate.
    if (!this.map?.isStyleLoaded()) return;
    this.disableMapFog();
    this.applyBasemapTheme();
    this.updateRouteLayerPaint();
  }

  private disableMapFog(): void {
    if (!this.map?.isStyleLoaded()) return;

    try {
      this.map.setFog(null);
    } catch (error) {
      console.warn('[ShipmentMap] Unable to disable map fog:', error);
    }
  }

  private applyBasemapTheme(): void {
    if (!this.map?.isStyleLoaded()) return;

    const lightPreset = this.config.lightPreset || (this.theme === 'dark' ? 'night' : 'day');

    try {
      this.map.setConfigProperty('basemap', 'theme', 'default');
      this.map.setConfigProperty('basemap', 'lightPreset', lightPreset);
      this.map.setConfigProperty('basemap', 'show3dObjects', false);
      this.map.setConfigProperty('basemap', 'show3dBuildings', false);
      this.map.setConfigProperty('basemap', 'show3dLandmarks', false);
      this.map.setConfigProperty('basemap', 'show3dTrees', false);
      this.disableMapFog();
    } catch (error) {
      console.warn('[ShipmentMap] Unable to apply basemap theme:', error);
    }
  }

  private getRoutePaint(layerConfig: LayerConfig): mapboxgl.LinePaint {
    return {
      'line-color': layerConfig.color[this.theme],
      'line-width': layerConfig.width,
      'line-emissive-strength': layerConfig.emissiveStrength?.[this.theme] || 0,
      ...(layerConfig.dashArray && { 'line-dasharray': layerConfig.dashArray })
    };
  }

  private updateRouteLayerPaint(): void {
    if (!this.map) return;

    Object.entries(ShipmentMap.LAYERS).forEach(([layerId, layerConfig]) => {
      if (!this.map?.getLayer(layerId)) return;

      const paint = this.getRoutePaint(layerConfig);
      Object.entries(paint).forEach(([property, value]) => {
        this.map?.setPaintProperty(layerId, property as keyof mapboxgl.AnyPaint, value);
      });
    });
  }

  /**
   * Add a marker to the map
   * 
   * @param type - Type of marker (origin, current, destination)
   * @param coordinates - [longitude, latitude]
   * @param className - Optional CSS class for custom styling
   * @param waypointId - Optional waypoint ID for edit functionality
   * @param isVerified - Optional verified status (adds 'verified' class)
   */
  addMarker(
    type: MarkerType, 
    coordinates: Coordinate, 
    className?: string,
    waypointId?: string,
    isVerified?: boolean
  ): mapboxgl.Marker | null {
    if (!this.map) {
      console.error('[ShipmentMap] Map not initialized');
      return null;
    }

    // Remove existing marker of this type if present
    this.removeMarker(type);

    let marker: mapboxgl.Marker;

    if (className) {
      const el = document.createElement('div');
      el.className = className;
      
      // Add verified class if waypoint is verified
      if (isVerified) {
        el.classList.add('verified');
        // console.log(`[ShipmentMap] Adding verified marker: ${type}, waypointId: ${waypointId}`);
      }
      
      // Add waypoint ID as data attribute for edit functionality
      if (waypointId) {
        el.dataset.waypointId = waypointId;
        el.style.cursor = 'pointer';
        el.title = 'Double-click to edit waypoint';
        
        // Add double-click handler with capture phase to intercept before delegation
        el.addEventListener('dblclick', (e) => {
          e.stopPropagation();
          e.stopImmediatePropagation(); // Stop ALL handlers including delegated ones
          e.preventDefault(); // Prevent default double-click behavior
          console.log('[ShipmentMap] Marker double-clicked, waypointId:', waypointId);
          
          // Call global tracker.editWaypoint if available
          if (typeof window !== 'undefined' && (window as any).tracker?.editWaypoint) {
            (window as any).tracker.editWaypoint(waypointId);
          } else {
            console.warn('[ShipmentMap] tracker.editWaypoint not available');
          }
        }, true); // Use capture phase to intercept event before it bubbles
      }
      
      marker = new mapboxgl.Marker(el);
    } else {
      marker = new mapboxgl.Marker();
    }

    marker.setLngLat(coordinates).addTo(this.map);
    this.markers.set(type, marker);

    return marker;
  }

  /**
   * Remove a specific marker from the map
   */
  removeMarker(type: MarkerType): void {
    const marker = this.markers.get(type);
    if (marker) {
      marker.remove();
      this.markers.delete(type);
    }
  }

  /**
   * Remove all markers from the map
   */
  clearMarkers(): void {
    this.markers.forEach(marker => marker.remove());
    this.markers.clear();
  }

  /**
   * Add a route layer to the map
   * 
   * @param layerId - Layer identifier ('route', 'route-final', or 'planned')
   * @param path - GeoJSON feature with LineString geometry
   */
  addRouteLayer(layerId: 'route' | 'route-final' | 'planned', path: Feature<LineString>): void {
    if (!this.map) {
      console.error('[ShipmentMap] Map not initialized');
      return;
    }

    const layerConfig = ShipmentMap.LAYERS[layerId];
    const geojson = {
      type: 'FeatureCollection' as const,
      features: [path]
    };

    // Remove existing layer/source if present
    this.removeLayer(layerId);

    // Add source
    this.map.addSource(layerId, {
      type: 'geojson',
      data: geojson
    });

    // Add layer
    const layerDef: mapboxgl.AnyLayer = {
      id: layerConfig.id,
      source: layerId,
      type: 'line',
      paint: this.getRoutePaint(layerConfig)
    };

    this.map.addLayer(layerDef);
  }

  /**
   * Remove a layer and its source from the map
   */
  removeLayer(layerId: string): void {
    if (!this.map) return;

    if (this.map.getLayer(layerId)) {
      this.map.removeLayer(layerId);
    }

    if (this.map.getSource(layerId)) {
      this.map.removeSource(layerId);
    }
  }

  /**
   * Fit map bounds to show all coordinates
   * 
   * @param coordinates - Array of coordinates to fit
   * @param options - Fit bounds options (padding, maxZoom, etc.)
   */
  fitBounds(
    coordinates: Coordinate[], 
    options?: Partial<mapboxgl.FitBoundsOptions>
  ): void {
    if (!this.map || coordinates.length < 2) return;

    const defaultOptions: mapboxgl.FitBoundsOptions = {
      padding: { top: 40, bottom: 60, left: 45, right: 45 },
      maxZoom: 10,
      easing: (t) => t
    };

    this.map.fitBounds(
      [coordinates[0], coordinates[coordinates.length - 1]],
      { ...defaultOptions, ...options }
    );
  }

  /**
   * Reset map to initial state (center, zoom, clear data)
   */
  reset(): void {
    if (!this.map) return;

    // Clear any pending center operation
    this.pendingCenter = null;

    // Jump to default position
    this.map.jumpTo({ center: [0, 0], zoom: 0 });

    // Remove all layers
    this.removeLayer('route');
    this.removeLayer('route-final');
    this.removeLayer('planned');

    // Remove all markers
    this.clearMarkers();
  }

  /**
   * Load shipment visualization on map
   * 
   * @param origin - Origin coordinates
   * @param current - Current location coordinates (or null)
   * @param destination - Destination coordinates (or null)
   * @param routePath - Route path feature
   * @param plannedPath - Planned route path feature
   * @param progressWaypoints - Array of intermediate waypoint coordinates (optional)
   * @param waypointIds - Object with origin, destination, and progress waypoint IDs (optional)
   * @param verifiedStatus - Object with verified flags for waypoints (optional)
   * @param isDelivered - Whether shipment has been delivered (affects route styling and map focus)
   */
  loadShipment(
    origin: Coordinate,
    current: Coordinate | null,
    destination: Coordinate | null,
    routePath: Feature<LineString> | false,
    plannedPath: Feature<LineString> | false,
    progressWaypoints?: Coordinate[],
    waypointIds?: {
      origin?: string;
      destination?: string;
      progress?: string[];
    },
    verifiedStatus?: {
      origin?: boolean;
      destination?: boolean;
      progress?: boolean[];
    },
    isDelivered?: boolean
  ): void {
    if (!this.map) {
      console.error('[ShipmentMap] Map not initialized');
      return;
    }

    // Add origin marker (editable if waypointId provided)
    this.addMarker(
      MarkerType.ORIGIN, 
      origin, 
      'marker-origin', 
      waypointIds?.origin,
      verifiedStatus?.origin
    );

    // Add current location marker if available
    const currentCoord = current || origin;
    if (current) {
      this.addMarker(MarkerType.CURRENT_LOCATION, current, 'marker-current');
    }

    // Add destination marker if available (editable if waypointId provided, green if verified)
    const destCoord = destination || origin;
    if (destination) {
      this.addMarker(
        MarkerType.DESTINATION, 
        destination, 
        'marker-destination',
        waypointIds?.destination,
        verifiedStatus?.destination
      );
    }

    // Add small grey markers for intermediate progress waypoints (excluding current location)
    if (progressWaypoints && progressWaypoints.length > 0) {
      progressWaypoints.forEach((waypointCoord, index) => {
        // Skip if this waypoint is the current location (already has its own marker)
        if (current && waypointCoord[0] === current[0] && waypointCoord[1] === current[1]) {
          return;
        }
        
        // Add small grey waypoint marker (editable if waypointId provided, green if verified)
        const waypointMarkerType = `${MarkerType.WAYPOINT}-${index}` as MarkerType;
        const waypointId = waypointIds?.progress?.[index];
        const isVerified = verifiedStatus?.progress?.[index];
        this.addMarker(
          waypointMarkerType, 
          waypointCoord, 
          'marker-waypoint',
          waypointId,
          isVerified
        );
      });
    }

    // Add route layers if paths are valid
    if (routePath) {
      this.addRouteLayer('route', routePath);
    }

    if (plannedPath) {
      // For delivered shipments, use solid line (route-final) instead of dotted
      if (isDelivered) {
        this.addRouteLayer('route-final', plannedPath);
      } else {
        this.addRouteLayer('planned', plannedPath);
      }
    }

    // For delivered shipments, focus on destination; otherwise focus on current location
    if (isDelivered && destination) {
      this.centerOnDestination(destination);
    } else {
      this.centerOnCurrentLocation(currentCoord, destCoord);
    }
  }

  /**
   * Center map on destination with appropriate zoom level
   * Used for delivered shipments to focus on final location
   * 
   * @param destination - Destination coordinates
   */
  centerOnDestination(destination: Coordinate): void {
    if (!this.map) return;

    // If map hasn't fully loaded yet, queue this operation
    if (!this.isLoaded) {
      // console.log('[ShipmentMap] Map not ready, queuing destination center operation');
      this.pendingCenter = { current: destination, destination };
      return;
    }

    // Check if container is visible (has dimensions)
    const container = this.map.getContainer();
    if (container.offsetWidth === 0 || container.offsetHeight === 0) {
      // console.log('[ShipmentMap] Container not visible, queuing destination center operation');
      this.pendingCenter = { current: destination, destination };
      return;
    }

    // Force map to recalculate its container size
    this.map.resize();

    // Use a close zoom level for destination focus (zoom 12 = city-level detail)
    const zoom = 12;
    
    // Use slower animation for smoother experience
    this.map.flyTo({
      center: destination,
      zoom: zoom,
      duration: 2500,  // 2.5 seconds for smooth animation
      essential: true
    });
  }

  /**
   * Center map on current location with zoom level based on distance to destination
   * 
   * @param current - Current location coordinates
   * @param destination - Destination coordinates (used to calculate appropriate zoom)
   */
  centerOnCurrentLocation(current: Coordinate, destination: Coordinate): void {
    if (!this.map) return;

    // Always store the last center coords for later recenter calls
    this.lastCenterCoords = { current, destination };

    // If map hasn't fully loaded yet, queue this operation
    if (!this.isLoaded) {
      // console.log('[ShipmentMap] Map not ready, queuing center operation');
      this.pendingCenter = { current, destination };
      return;
    }

    // Check if container is visible (has dimensions)
    const container = this.map.getContainer();
    if (container.offsetWidth === 0 || container.offsetHeight === 0) {
      // console.log('[ShipmentMap] Container not visible, queuing center operation');
      this.pendingCenter = { current, destination };
      return;
    }

    // Force map to recalculate its container size
    this.map.resize();

    // Calculate distance to destination in miles
    const distanceToDestMiles = calculateStraightDistance(current, destination);
    
    // Calculate zoom level based on remaining distance
    const zoom = this.calculateZoomForDistance(distanceToDestMiles);
    
    // Use slower animation for smoother experience
    this.map.flyTo({
      center: current,
      zoom: zoom,
      duration: 2500,  // 2.5 seconds for smooth animation
      easing: (t) => t * (2 - t)  // Ease out quad for natural deceleration
    });
  }

  /**
   * Recenter map using the last stored coordinates
   * Call this after the modal becomes visible (shown.bs.modal event)
   */
  recenter(): void {
    if (!this.map) return;
    
    // Resize first to ensure container dimensions are correct
    this.map.resize();
    
    // If we have stored coords, use them
    if (this.lastCenterCoords) {
      this.centerOnCurrentLocation(
        this.lastCenterCoords.current,
        this.lastCenterCoords.destination
      );
    } else if (this.pendingCenter) {
      // Fallback to pending center if available
      this.centerOnCurrentLocation(
        this.pendingCenter.current,
        this.pendingCenter.destination
      );
      this.pendingCenter = null;
    }
  }

  /**
   * Calculate appropriate zoom level based on distance in miles
   * 
   * Zoom levels (approximate coverage):
   * - 2: ~5000+ miles (continental/global)
   * - 4: ~1500-5000 miles (country scale)  
   * - 5: ~500-1500 miles (regional)
   * - 6: ~200-500 miles (state/province)
   * - 7: ~100-200 miles (multi-city)
   * - 8: ~50-100 miles (metro area)
   * - 9: ~20-50 miles (city)
   * - 10: <20 miles (neighborhood)
   * 
   * @param distanceMiles - Distance to destination in miles
   * @returns Appropriate zoom level (2-10)
   */
  private calculateZoomForDistance(distanceMiles: number): number {
    if (distanceMiles > 5000) return 2;       // Intercontinental
    if (distanceMiles > 2000) return 3;       // Transcontinental
    if (distanceMiles > 1000) return 4;       // Cross-country
    if (distanceMiles > 500) return 5;        // Regional
    if (distanceMiles > 200) return 6;        // State/Province
    if (distanceMiles > 100) return 7;        // Multi-city
    if (distanceMiles > 50) return 8;         // Metro area
    if (distanceMiles > 20) return 9;         // City
    return 10;                                 // Neighborhood/close
  }

  /**
   * Fly to a specific location on the map
   * Used for clicking waypoints in SVG distance bar or activity feed
   * 
   * @param coordinates - [longitude, latitude] to fly to
   * @param zoom - Zoom level (default: 4 for regional view)
   */
  flyToLocation(coordinates: Coordinate, zoom: number = 4): void {
    if (!this.map) return;

    this.map.flyTo({
      center: coordinates,
      zoom: zoom,
      duration: 1500,  // 1.5 seconds
      easing: (t) => t * (2 - t)  // Ease out quad
    });
  }

  /**
   * Destroy the map instance and clean up resources
   */
  destroy(): void {
    if (this.map) {
      this.clearMarkers();
      this.map.remove();
      this.map = null;
    }
  }

  /**
   * Get the default origin coordinates (Voltera office)
   */
  static getDefaultOrigin(): Coordinate {
    return ShipmentMap.DEFAULT_ORIGIN;
  }
}
